import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import type { SheetMirrorColumnDto } from '@bhops/shared';
import { dateLike } from './sheetDates';
import { Icon } from '@/components/Icon';
import { kstTodayYmd, toLocalYmd } from '@/lib/format-date';
import {
  SheetTable,
  SheetTh,
  headerBaseBg,
  SheetValue,
  chipColorOf,
  sheetClip,
  sheetTableWrap,
  sheetTd,
  type SheetChipColors,
  type SheetSort,
  type SheetWidths,
} from './sheetTable';

// 시트처럼 칸에서 바로 고치는 표(2026-10-23 오너 — "그냥 스프레드 시트에 입력하는거마냥 돼야해").
//   · 누르면 칸 고르기, 두 번 누르기 · Enter · F2 · 바로 글자 치기 → 그 칸에서 고치기
//   · Enter = 저장 후 아래 칸, Tab = 저장 후 오른쪽 칸, Esc = 취소, 화살표 = 칸 옮기기, Delete = 칸 비우기
//   · 드롭다운 칸은 그 자리에서 시트와 같은 칩 목록, 여러 개 고르기(상담원)는 눌러 켜고 끄기
//   · 긴 글 칸(비고)은 칸 아래로 펼쳐 쓰고, 줄바꿈은 Alt+Enter(시트와 같다)
//   · 끌기 · Shift+누르기 · Shift+화살표로 여러 칸을 잡는다(2026-10-26 오너 "드래그하면 여러 영역 잡을 수 있게") —
//     Ctrl+C 는 잡은 칸을 시트에 그대로 붙는 모양(탭 · 줄바꿈)으로 복사, Delete 는 잡은 칸 중 고칠 수 있는 칸을 비운다
// 한글: 고른 칸에는 보이지 않는 입력칸이 늘 떠 있고, 글자를 치면 그 입력칸이 그대로 편집기가 된다 —
// 편집기를 새로 띄우면 IME 조합(ㅎ→하→한)이 끊겨 첫 글자를 잃는다. 조합 중 Enter 는 저장이 아니다.

export interface SheetGridRow {
  /** 줄 열쇠 — 화면 안에서 하나 */
  key: string;
  /** 칸 열쇠 → 보이는 글자 */
  cells: Record<string, string>;
  /** 칸 열쇠 → 고칠 때 시작 값(날짜 칸 'YYYY-MM-DD') */
  values: Record<string, string>;
  /** 칸 열쇠 → 시트 칸 바탕색 */
  bg?: Record<string, string>;
  /** 줄 전체 바탕(시트 조건부 서식처럼) — 칸 바탕색보다 먼저 */
  fill?: string;
  /** 강조한 줄(다른 화면에서 이 줄을 찾아 들어옴) */
  mark?: boolean;
  /** 못 고치는 칸(수식) — 줄 전체를 막으려면 lockedRow */
  locked?: ReadonlySet<string>;
  lockedRow?: boolean;
  /** 아직 시트에 안 쓴 칸(기울임 · 노랑) */
  pending?: ReadonlySet<string>;
  /** 줄 바탕 — 못 씀(빨강) · 새 줄 작성 중(옅은 브랜드색) · 새 줄 쓰는 중(노랑) */
  tone?: 'pending' | 'problem' | 'draft';
  title?: string;
  testId?: string;
}

type Pos = { r: number; c: number };
/** 잡은 칸 묶음 — 위 · 아래 줄, 왼 · 오른 열(끝 포함) */
type Rect = { r0: number; r1: number; c0: number; c1: number };
type HistoryKey = Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>;
type Next = { dr: number; dc: number };
/** 고치는 중 — text: 칸 위 입력칸, list: 칩 목록, date: 날짜 고르기. at = 그 칸('r:c') */
type Mode = { kind: 'text' | 'list' | 'date'; value: string; at: string } | null;
const keyOf = (p: Pos) => `${p.r}:${p.c}`;
/** 잡은 칸 묶음의 바깥 테두리만 — 칸마다 자기 쪽 변 */
function rectEdges(b: Rect, r: number, c: number): string | undefined {
  const e: string[] = [];
  if (r === b.r0) e.push('inset 0 1px 0 0 var(--brand)');
  if (r === b.r1) e.push('inset 0 -1px 0 0 var(--brand)');
  if (c === b.c0) e.push('inset 1px 0 0 0 var(--brand)');
  if (c === b.c1) e.push('inset -1px 0 0 0 var(--brand)');
  return e.length ? e.join(', ') : undefined;
}
/** 잡은 칸 위에 옅은 브랜드색 — 칸 바탕색(시트 색)은 그대로 비친다 */
const rangeTint: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'color-mix(in srgb, var(--brand) 14%, transparent)',
  pointerEvents: 'none',
};

export function SheetGrid({
  columns,
  rows,
  widths,
  colors,
  longKeys,
  readOnly,
  onCommit,
  focusRow,
  sort,
  onSort,
  onUndo,
  onRedo,
}: {
  columns: SheetMirrorColumnDto[];
  rows: SheetGridRow[];
  widths?: SheetWidths;
  colors?: SheetChipColors;
  /** 왼쪽 정렬 · 펼쳐 쓰는 긴 글 칸 */
  longKeys?: ReadonlySet<string>;
  readOnly?: boolean;
  /** 한 칸 저장 — 끝나면 true(실패면 false) */
  onCommit: (row: SheetGridRow, col: SheetMirrorColumnDto, value: string) => Promise<boolean>;
  /** 이 열쇠의 줄 첫 칸을 고른다(새 줄 만들 때) */
  focusRow?: string | null;
  /** 머리글을 눌러 콘솔에서만 정렬 */
  sort?: SheetSort;
  onSort?: (next: SheetSort) => void;
  /** Ctrl+Z 되돌리기 · Ctrl+Y(또는 Ctrl+Shift+Z) 다시 하기 — 칸을 고치는 중에는 입력칸의 글자 되돌리기 */
  onUndo?: () => void;
  onRedo?: () => void;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const baseBg = useMemo(() => headerBaseBg(columns), [columns]);
  /** 날짜 칸마다 보이는 글자 하나 — 저장 중 값을 같은 모양('10월21일')으로 */
  const dateSample = useMemo(() => {
    const out: Record<string, string> = {};
    for (const c of columns)
      if (c.type === 'date') {
        const r = rows.find(
          (x) =>
            /^\d{4}-/.test(x.values[c.key] ?? '') &&
            x.cells[c.key] &&
            !/^\d{4}-\d{2}-\d{2}$/.test(x.cells[c.key]!),
        );
        if (r) out[c.key] = r.cells[c.key]!;
      }
    return out;
  }, [columns, rows]);
  const [sel, setSel] = useState<Pos | null>(null);
  /** 여러 칸을 잡았을 때 고른 칸(sel)의 맞은편 끝 — 없으면 한 칸 */
  const [ext, setExt] = useState<Pos | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  /** 누르고 끄는 중 — moved: 다른 칸까지 끌었다, open: 떼면 목록 · 달력을 연다(드롭다운 · 날짜 칸) */
  const drag = useRef<{ p: Pos; moved: boolean; open: boolean } | null>(null);
  /** 저장 중인 칸 값 — 서버 답이 오기 전에도 고친 값이 보이게 */
  const [saving, setSaving] = useState<Record<string, string>>({});

  // 새 줄이 생기면 그 줄 첫 칸을 고른다(한 번만)
  const focused = useRef<string | null>(null);
  useEffect(() => {
    if (!focusRow || focused.current === focusRow) return;
    const r = rows.findIndex((x) => x.key === focusRow);
    if (r < 0) return;
    focused.current = focusRow;
    setMode(null);
    setSel({ r, c: 0 });
    setExt(null);
  }, [focusRow, rows]);

  // 고른 칸(넓히는 중이면 넓힌 끝 칸)이 보이게
  const shown = ext ?? sel;
  useEffect(() => {
    if (!shown) return;
    wrap.current
      ?.querySelector<HTMLElement>(`[data-cell="${shown.r}:${shown.c}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [shown?.r, shown?.c]);

  /** 잡은 칸 묶음 — 여러 칸일 때만 */
  const rect: Rect | null =
    sel && ext && (ext.r !== sel.r || ext.c !== sel.c)
      ? {
          r0: Math.max(0, Math.min(sel.r, ext.r)),
          r1: Math.min(rows.length - 1, Math.max(sel.r, ext.r)),
          c0: Math.max(0, Math.min(sel.c, ext.c)),
          c1: Math.min(columns.length - 1, Math.max(sel.c, ext.c)),
        }
      : null;
  const pick = (p: Pos | null) => {
    setSel(p);
    setExt(null);
  };

  const canEdit = (p: Pos) => {
    const rw = rows[p.r];
    const cl = columns[p.c];
    return !!rw && !!cl && !readOnly && !rw.lockedRow && !rw.locked?.has(cl.key);
  };
  const isList = (cl: SheetMirrorColumnDto) => cl.type === 'list' && !!cl.options?.length;

  const move = (p: Pos, n: Next): Pos => ({
    r: Math.min(Math.max(p.r + n.dr, 0), rows.length - 1),
    c: Math.min(Math.max(p.c + n.dc, 0), columns.length - 1),
  });

  /** 한 칸 저장 — 서버 답이 올 때까지 고친 값을 기울여 보여 준다. */
  const save = async (p: Pos, value: string) => {
    const rw = rows[p.r];
    const cl = columns[p.c];
    if (!rw || !cl || value === (rw.values[cl.key] ?? '')) return;
    const id = `${rw.key}|${cl.key}`;
    setSaving((s) => ({ ...s, [id]: value }));
    try {
      await onCommit(rw, cl, value);
    } finally {
      setSaving((s) => {
        const n = { ...s };
        delete n[id];
        return n;
      });
    }
  };

  /** 고치기 시작 — 글자 칸은 칸 위 입력칸이, 드롭다운 · 날짜 칸은 고르기 창이 뜬다. */
  const begin = (p: Pos) => {
    if (!canEdit(p)) return;
    const cl = columns[p.c]!;
    const value = rows[p.r]!.values[cl.key] ?? '';
    const kind = isList(cl) ? 'list' : cl.type === 'date' ? 'date' : 'text';
    setMode({ kind, value, at: keyOf(p) });
  };

  /**
   * 그 칸 고치기를 끝낸다. 다른 칸을 눌러 이미 옮겨 갔으면(고르기 창이 사라지며 부름) 저장만 하고
   * 지금 고른 칸 · 고치기는 건드리지 않는다.
   */
  const end = (p: Pos, value: string | null, next?: Next) => {
    const at = keyOf(p);
    setMode((m) => (m?.at === at ? null : m));
    if (next) {
      setSel((cur) => (cur && keyOf(cur) === at ? move(p, next) : cur));
      setExt(null);
    }
    if (value !== null) void save(p, value);
  };

  /** 잡은 칸(없으면 고른 칸)을 시트에 그대로 붙는 글자로 — 칸은 탭, 줄은 줄바꿈 */
  const copyText = () => {
    if (!sel) return '';
    const b = rect ?? { r0: sel.r, r1: sel.r, c0: sel.c, c1: sel.c };
    const out: string[] = [];
    for (let r = b.r0; r <= b.r1; r++) {
      const line: string[] = [];
      for (let c = b.c0; c <= b.c1; c++) {
        const t = rows[r]?.cells[columns[c]!.key] ?? '';
        // 칸 안 줄바꿈 · 탭이 있으면 시트처럼 따옴표로 감싼다
        line.push(/[\t\n"]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t);
      }
      out.push(line.join('\t'));
    }
    return out.join('\n');
  };

  /** Delete — 잡은 칸 중 고칠 수 있고 값이 있는 칸을 비운다(한 칸씩 저장 · Ctrl+Z 도 한 칸씩) */
  const clearCells = () => {
    if (!sel) return;
    const b = rect ?? { r0: sel.r, r1: sel.r, c0: sel.c, c1: sel.c };
    for (let r = b.r0; r <= b.r1; r++)
      for (let c = b.c0; c <= b.c1; c++) {
        const q = { r, c };
        if (canEdit(q) && (rows[r]!.values[columns[c]!.key] ?? '') !== '') void save(q, '');
      }
  };

  // 끌기를 마치면 — 다른 칸까지 끌지 않았고 드롭다운 · 날짜 칸이면 그때 목록 · 달력을 연다
  const latestBegin = useRef(begin);
  latestBegin.current = begin;
  useEffect(() => {
    const up = () => {
      const d = drag.current;
      drag.current = null;
      if (d && !d.moved && d.open) latestBegin.current(d.p);
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  /** 되돌리기 키면 true — 칸을 고치는 중(입력칸 · 고르기 창)이면 입력칸 몫으로 둔다 */
  const history = (e: HistoryKey) => {
    if (mode || readOnly || !(e.ctrlKey || e.metaKey) || e.altKey) return false;
    const k = e.key.toLowerCase();
    if (k === 'z' && !e.shiftKey) onUndo?.();
    else if (k === 'y' || (k === 'z' && e.shiftKey)) onRedo?.();
    else return false;
    return true;
  };
  // 표 밖 빈 곳(body)에 초점이 있어도 받는다 — 다른 입력칸에 있으면 그쪽 몫
  const latestHistory = useRef(history);
  latestHistory.current = history;
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.target !== document.body) return;
      if (latestHistory.current(e)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      ref={wrap}
      style={sheetTableWrap}
      data-testid="sheet-grid"
      aria-label="시트 표. 칸을 고르고 Enter로 고칩니다. Ctrl+Z로 되돌리고 Ctrl+Y로 다시 합니다."
      onKeyDown={(e) => {
        if (e.nativeEvent.isComposing) return;
        if (history(e)) e.preventDefault();
      }}
    >
      <SheetTable columns={columns} widths={widths}>
        <thead>
          <tr>
            {columns.map((c) => (
              <SheetTh
                key={c.key}
                col={c}
                widths={widths}
                sort={sort}
                baseBg={baseBg}
                onSort={
                  onSort &&
                  ((next) => {
                    // 줄 순서가 바뀌므로 고른 칸은 푼다
                    setMode(null);
                    pick(null);
                    onSort(next);
                  })
                }
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rw, r) => (
            <tr
              key={rw.key}
              style={{ background: rw.tone ? TONE[rw.tone] : undefined }}
              title={rw.title}
              data-testid={rw.testId}
            >
              {columns.map((cl, c) => {
                const id = `${rw.key}|${cl.key}`;
                const p = { r, c };
                const isSel = sel?.r === r && sel.c === c;
                const inRect =
                  !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;
                const here = mode?.at === keyOf(p) ? mode : null;
                const savingValue = saving[id];
                const text =
                  savingValue !== undefined
                    ? cl.type === 'date'
                      ? dateLike(savingValue, dateSample[cl.key])
                      : savingValue
                    : (rw.cells[cl.key] ?? '');
                const italic = savingValue !== undefined || !!rw.pending?.has(cl.key);
                const long = !!longKeys?.has(cl.key);
                const cellEditable = !readOnly && !rw.lockedRow && !rw.locked?.has(cl.key);
                return (
                  <td
                    key={cl.key}
                    data-cell={`${r}:${c}`}
                    style={{
                      ...sheetTd,
                      position: 'relative',
                      cursor: cellEditable ? 'cell' : 'default',
                      // 줄 색(못 씀 · 새 줄)이 먼저, 그다음 시트에 쓰는 중인 칸(노랑),
                      // 그다음 줄 전체 색(조건부 서식처럼), 그다음 시트 칸 색
                      ...(rw.tone
                        ? null
                        : italic
                          ? { background: TONE.pending }
                          : rw.fill || rw.bg?.[cl.key]
                            ? { background: rw.fill ?? rw.bg?.[cl.key] }
                            : null),
                      ...(rw.mark ? MARK : null),
                      ...(isSel
                        ? { boxShadow: 'inset 0 0 0 2px var(--brand)' }
                        : inRect
                          ? { boxShadow: rectEdges(rect!, r, c) }
                          : null),
                    }}
                    title={text}
                    data-in-range={inRect || undefined}
                    onMouseDown={(e) => {
                      // 고치는 중인 칸을 다시 누름 — 드롭다운은 닫히고(blur), 입력칸은 그대로
                      if (here) return;
                      e.preventDefault();
                      // 고치던 값은 그 편집기가 사라지며 저장한다
                      setMode(null);
                      if (e.shiftKey && sel) {
                        setExt(p);
                        return;
                      }
                      pick(p);
                      // 드롭다운 칸은 한 번 누르면 바로 목록, 날짜 칸은 달력(2026-10-23 오너) — 끌기와 가르려고
                      // 손을 뗄 때 연다(다른 칸까지 끌었으면 열지 않고 여러 칸을 잡는다).
                      drag.current = {
                        p,
                        moved: false,
                        open: (isList(cl) || cl.type === 'date') && cellEditable,
                      };
                    }}
                    onMouseEnter={(e) => {
                      const d = drag.current;
                      if (!d) return;
                      if (!(e.buttons & 1)) {
                        drag.current = null;
                        return;
                      }
                      if (d.p.r !== r || d.p.c !== c) d.moved = true;
                      setExt(p);
                    }}
                    onDoubleClick={() => {
                      if (isSel && !here) begin(p);
                    }}
                  >
                    <div
                      style={{
                        ...sheetClip,
                        textAlign: long ? 'left' : 'center',
                        fontStyle: italic ? 'italic' : undefined,
                        visibility: here?.kind === 'text' ? 'hidden' : undefined,
                      }}
                    >
                      <SheetValue col={cl} value={text} colors={colors} />
                    </div>
                    {inRect && !isSel && <div style={rangeTint} aria-hidden />}
                    {isSel && (
                      <CellInput
                        key={id}
                        col={cl}
                        long={long}
                        editable={cellEditable}
                        editing={here?.kind === 'text'}
                        busy={!!here}
                        start={rw.values[cl.key] ?? ''}
                        onBegin={() => begin(p)}
                        onType={(typed) => setMode({ kind: 'text', value: typed, at: keyOf(p) })}
                        onNav={(n) => {
                          setSel((cur) => (cur ? move(cur, n) : cur));
                          setExt(null);
                        }}
                        onExtend={(n) => setExt((x) => move(x ?? p, n))}
                        onClear={clearCells}
                        copyText={copyText}
                        onEnd={(v, n) => end(p, v, n)}
                        onUnselect={() => pick(null)}
                      />
                    )}
                    {here?.kind === 'list' && (
                      <ListPicker
                        col={cl}
                        initial={here.value}
                        colors={colors}
                        anchor={keyOf(p)}
                        onDone={(v, n) => end(p, v, n)}
                      />
                    )}
                    {here?.kind === 'date' && (
                      <DatePicker
                        col={cl}
                        initial={here.value}
                        anchor={keyOf(p)}
                        onDone={(v, n) => end(p, v, n)}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </SheetTable>
    </div>
  );
}

/**
 * 고른 칸에 늘 떠 있는 입력칸. 안 고칠 때는 보이지 않고(키보드만 받는다), 글자를 치면 그대로 편집기가 된다.
 */
function CellInput({
  col,
  long,
  editable,
  editing,
  busy,
  start,
  onBegin,
  onType,
  onNav,
  onExtend,
  onClear,
  copyText,
  onEnd,
  onUnselect,
}: {
  col: SheetMirrorColumnDto;
  long: boolean;
  editable: boolean;
  /** 이 입력칸으로 글자를 고치는 중 */
  editing: boolean;
  /** 이 칸을 고치는 중(드롭다운 · 날짜 창 포함) — 끝나면 다시 키보드를 받는다 */
  busy: boolean;
  start: string;
  onBegin: () => void;
  onType: (typed: string) => void;
  onNav: (n: Next) => void;
  /** Shift+화살표 — 잡은 칸을 넓힌다 */
  onExtend: (n: Next) => void;
  /** Delete — 고른(잡은) 칸 비우기 */
  onClear: () => void;
  /** Ctrl+C 로 복사할 글자 */
  copyText: () => string;
  onEnd: (value: string | null, next?: Next) => void;
  onUnselect: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [v, setV] = useState('');
  /** 글자를 쳐서 시작했나(그러면 기존 값을 지우고 친 글자부터) */
  const typedStart = useRef(false);
  const done = useRef(false);
  const textual = col.type !== 'list' && col.type !== 'date';

  // 고르면 곧바로 키보드를 받는다. 고치다가 다른 칸을 누르면(이 입력칸이 사라짐) 고친 값을 저장한다.
  const latest = useRef({ v, editing, onEnd });
  latest.current = { v, editing, onEnd };
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    return () => {
      const l = latest.current;
      if (l.editing && !done.current) l.onEnd(l.v);
    };
  }, []);

  // Enter · F2 · 두 번 누르기로 시작하면 기존 값을 채우고 커서를 끝에.
  const wasEditing = useRef(false);
  useLayoutEffect(() => {
    if (editing && !wasEditing.current) {
      done.current = false;
      if (!typedStart.current) {
        setV(start);
        requestAnimationFrame(() => {
          const t = ref.current;
          if (t) t.setSelectionRange(t.value.length, t.value.length);
        });
      }
      typedStart.current = false;
    }
    wasEditing.current = editing;
  }, [editing, start]);
  // 고치기(글자 · 드롭다운 · 날짜)가 끝나면 다시 키보드를 받는다
  const wasBusy = useRef(false);
  useEffect(() => {
    if (!busy && wasBusy.current) ref.current?.focus({ preventScroll: true });
    wasBusy.current = busy;
  }, [busy]);

  const finish = (value: string | null, next?: Next) => {
    if (done.current) return;
    done.current = true;
    setV('');
    onEnd(value, next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // 한글 조합 중 키(Enter 등)는 조합을 마치는 데 쓴다
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const k = e.key;
    if (!editing) {
      const nav = (n: Next) => {
        e.preventDefault();
        if (e.shiftKey && k.startsWith('Arrow')) onExtend(n);
        else onNav(n);
      };
      // Ctrl+C — 잡은 칸 글자를 이 입력칸에 잠깐 담아 골라 두면 브라우저가 그대로 복사한다(권한 묻지 않음)
      if ((e.ctrlKey || e.metaKey) && !e.altKey && k.toLowerCase() === 'c') {
        const t = e.currentTarget;
        t.value = copyText();
        t.select();
        setTimeout(() => {
          t.value = '';
        }, 0);
        return;
      }
      if (k === 'ArrowUp') return nav({ dr: -1, dc: 0 });
      if (k === 'ArrowDown') return nav({ dr: 1, dc: 0 });
      if (k === 'ArrowLeft') return nav({ dr: 0, dc: -1 });
      if (k === 'ArrowRight') return nav({ dr: 0, dc: 1 });
      if (k === 'Tab') return nav({ dr: 0, dc: e.shiftKey ? -1 : 1 });
      if (k === 'Escape') {
        e.preventDefault();
        return onUnselect();
      }
      if (k === 'Enter' || k === 'F2') {
        e.preventDefault();
        return onBegin();
      }
      if (k === 'Delete' || k === 'Backspace') {
        e.preventDefault();
        return onClear();
      }
      // 드롭다운 · 날짜 칸 · 못 고치는 칸은 글자를 받지 않는다(Enter 로 고른다)
      if (k.length === 1 && !e.ctrlKey && !e.metaKey && (!textual || !editable)) e.preventDefault();
      return;
    }
    if (k === 'Escape') {
      e.preventDefault();
      return finish(null);
    }
    if (k === 'Tab') {
      e.preventDefault();
      return finish(v, { dr: 0, dc: e.shiftKey ? -1 : 1 });
    }
    if (k === 'Enter') {
      e.preventDefault();
      // 시트처럼 Alt+Enter(또는 Shift+Enter)는 칸 안 줄바꿈
      if (long && (e.altKey || e.shiftKey)) {
        const t = e.currentTarget;
        const a = t.selectionStart;
        const b = t.selectionEnd;
        setV(`${v.slice(0, a)}\n${v.slice(b)}`);
        requestAnimationFrame(() => t.setSelectionRange(a + 1, a + 1));
        return;
      }
      return finish(v, { dr: 1, dc: 0 });
    }
  };

  /** 안 고칠 때 글자가 들어오면(한글 조합 시작 포함) 그 자리에서 고치기 시작 */
  const startTyping = (value: string) => {
    if (editing || !editable || !textual) return;
    typedStart.current = true;
    onType(value);
  };

  return (
    <textarea
      ref={ref}
      value={v}
      rows={1}
      aria-label={col.key}
      spellCheck={false}
      onChange={(e) => {
        if (!editing && (!editable || !textual)) return setV('');
        setV(e.target.value);
        startTyping(e.target.value);
      }}
      onCompositionStart={() => startTyping(v)}
      onKeyDown={onKeyDown}
      onBlur={() => {
        if (editing) finish(v);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      style={editing ? { ...editorBox, ...(long ? longBox : inBox) } : ghostBox}
    />
  );
}

function ListPicker({
  col,
  initial,
  colors,
  anchor,
  onDone,
}: {
  col: SheetMirrorColumnDto;
  initial: string;
  colors?: SheetChipColors;
  /** 칸 위치 — 목록을 그 칸 바로 아래 화면 기준으로 띄운다(표 상자에 잘리지 않게) */
  anchor: string;
  onDone: (value: string | null, next?: Next) => void;
}) {
  const options = col.options ?? [];
  const split = (s: string) =>
    s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  const [picked, setPicked] = useState<string[]>(col.multi ? split(initial) : []);
  const [hi, setHi] = useState(Math.max(0, options.indexOf(initial.trim())));
  const [at, setAt] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  const done = useRef(false);
  const box = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const cell = document.querySelector<HTMLElement>(`[data-cell="${anchor}"]`);
    if (!cell) return;
    const r = cell.getBoundingClientRect();
    const h = Math.min(320, options.length * 28 + 48);
    const top = r.bottom + h > window.innerHeight ? Math.max(8, r.top - h) : r.bottom;
    setAt({ top, left: r.left, minWidth: Math.max(r.width, 200) });
  }, [anchor, options.length]);
  // 화면 오른쪽 끝 칸이면 목록을 왼쪽으로 당긴다
  useLayoutEffect(() => {
    const el = box.current;
    if (!at || !el) return;
    const over = el.getBoundingClientRect().right - (window.innerWidth - 8);
    if (over > 0) setAt({ ...at, left: Math.max(8, at.left - over) });
  }, [at]);
  useEffect(() => box.current?.focus({ preventScroll: true }), [at]);

  const end = (value: string | null, next?: Next) => {
    if (done.current) return;
    done.current = true;
    onDone(value, next);
  };
  const multiValue = (list: string[]) => options.filter((o) => list.includes(o)).join(', ');
  // 여러 개 고르기 중 다른 칸을 누르면(목록이 사라짐) 고른 것을 저장한다
  const latest = useRef({ picked, onDone });
  latest.current = { picked, onDone };
  useEffect(
    () => () => {
      if (done.current || !col.multi) return;
      const value = multiValue(latest.current.picked);
      if (value !== multiValue(split(initial))) latest.current.onDone(value);
    },
    [],
  );
  const choose = (o: string) => {
    if (!col.multi) return end(o, { dr: 1, dc: 0 });
    setPicked((p) => (p.includes(o) ? p.filter((x) => x !== o) : [...p, o]));
  };

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      e.preventDefault();
      return end(null);
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.min(Math.max(h + (e.key === 'ArrowDown' ? 1 : -1), 0), options.length - 1));
      return;
    }
    if (e.key === ' ' && col.multi) {
      e.preventDefault();
      return choose(options[hi]!);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      return col.multi ? end(multiValue(picked), { dr: 1, dc: 0 }) : choose(options[hi]!);
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      return end(col.multi ? multiValue(picked) : null, { dr: 0, dc: e.shiftKey ? -1 : 1 });
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      return end('', { dr: 1, dc: 0 });
    }
  };

  if (!at) return null;
  return (
    <div
      ref={box}
      tabIndex={-1}
      role="listbox"
      aria-label={col.key}
      aria-multiselectable={col.multi || undefined}
      onKeyDown={keys}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (box.current?.contains(e.relatedTarget as Node)) return;
        end(col.multi ? multiValue(picked) : null);
      }}
      style={{ ...listBox, ...at }}
    >
      {options.map((o, i) => {
        const on = col.multi ? picked.includes(o) : o === initial.trim();
        const [bg, fg] = chipColorOf(colors?.[col.key], o);
        return (
          <div
            key={o}
            role="option"
            aria-selected={on}
            onMouseEnter={() => setHi(i)}
            onClick={() => choose(o)}
            style={{ ...listItem, background: i === hi ? 'var(--bg-sunken)' : undefined }}
          >
            <span style={{ width: 14, display: 'inline-flex', color: 'var(--brand)' }}>
              {on && <Icon name="check" size={14} />}
            </span>
            <span style={{ ...chipLike, background: bg, color: fg }}>{o}</span>
          </div>
        );
      })}
      <div style={listFoot}>
        <button type="button" style={linkLike} onClick={() => end('', { dr: 1, dc: 0 })}>
          비우기
        </button>
        {col.multi && (
          <button
            type="button"
            style={{ ...linkLike, marginLeft: 'auto', fontWeight: 600 }}
            onClick={() => end(multiValue(picked), { dr: 1, dc: 0 })}
          >
            확인(Enter)
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * 날짜 칸 달력(2026-10-23 오너 "문의 일자 클릭하면 달력 나오고, 달력에서 일자 선택하면 시트 폼 안 해치는 값으로").
 * 고른 날짜는 'YYYY-MM-DD' 로 넘긴다 — 스크립트가 시트에 날짜 값으로 써서 칸의 표시 형식('10. 21' ·
 * '10월21일')은 시트 그대로 남는다. 주는 월요일부터(시트 '주차'와 같은 셈).
 */
function DatePicker({
  col,
  initial,
  anchor,
  onDone,
}: {
  col: SheetMirrorColumnDto;
  initial: string;
  anchor: string;
  onDone: (value: string | null, next?: Next) => void;
}) {
  const start = parseIso(initial) ?? today();
  const [cursor, setCursor] = useState(start);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const done = useRef(false);
  const box = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const cell = document.querySelector<HTMLElement>(`[data-cell="${anchor}"]`);
    if (!cell) return;
    const r = cell.getBoundingClientRect();
    const h = 300;
    const top = r.bottom + h > window.innerHeight ? Math.max(8, r.top - h) : r.bottom;
    setAt({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - 260)) });
  }, [anchor]);
  useEffect(() => box.current?.focus({ preventScroll: true }), [at]);

  const end = (value: string | null, next?: Next) => {
    if (done.current) return;
    done.current = true;
    onDone(value, next);
  };
  const pick = (d: Date) => end(toLocalYmd(d), { dr: 1, dc: 0 });
  const shift = (days: number) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth(), c.getDate() + days));
  const shiftMonth = (n: number) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, Math.min(c.getDate(), 28)));

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const k = e.key;
    const move: Record<string, () => void> = {
      ArrowLeft: () => shift(-1),
      ArrowRight: () => shift(1),
      ArrowUp: () => shift(-7),
      ArrowDown: () => shift(7),
      PageUp: () => shiftMonth(-1),
      PageDown: () => shiftMonth(1),
    };
    if (move[k]) {
      e.preventDefault();
      return move[k]!();
    }
    if (k === 'Escape') {
      e.preventDefault();
      return end(null);
    }
    if (k === 'Enter') {
      e.preventDefault();
      return pick(cursor);
    }
    if (k === 'Tab') {
      e.preventDefault();
      return end(null, { dr: 0, dc: e.shiftKey ? -1 : 1 });
    }
    if (k === 'Delete' || k === 'Backspace') {
      e.preventDefault();
      return end('', { dr: 1, dc: 0 });
    }
  };

  if (!at) return null;
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const lead = (first.getDay() + 6) % 7; // 월요일 0
  const days = new Date(y, m + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: days }, (_, i) => new Date(y, m, i + 1)),
  ];
  const chosen = initial ? initial.slice(0, 10) : '';
  const todayIso = kstTodayYmd();
  return (
    <div
      ref={box}
      tabIndex={-1}
      role="dialog"
      aria-label={`${col.key} 달력`}
      onKeyDown={keys}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (box.current?.contains(e.relatedTarget as Node)) return;
        end(null);
      }}
      style={{ ...listBox, ...at, width: 244, padding: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
        <button type="button" style={calNav} onClick={() => shiftMonth(-1)} aria-label="지난달">
          <Icon name="chev-left" size={14} />
        </button>
        <strong style={{ flex: 1, textAlign: 'center', fontSize: 13 }}>
          {y}년 {m + 1}월
        </strong>
        <button type="button" style={calNav} onClick={() => shiftMonth(1)} aria-label="다음달">
          <Icon name="chev-right" size={14} />
        </button>
      </div>
      <div style={calGrid}>
        {['월', '화', '수', '목', '금', '토', '일'].map((d) => (
          <span
            key={d}
            style={{ ...calHead, color: d === '일' ? 'var(--fg-danger, #c62828)' : undefined }}
          >
            {d}
          </span>
        ))}
        {cells.map((d, i) => {
          if (!d) return <span key={`b${i}`} />;
          const iso = toLocalYmd(d);
          const on = iso === chosen;
          const hi = iso === toLocalYmd(cursor);
          return (
            <button
              key={iso}
              type="button"
              aria-label={iso}
              aria-pressed={on}
              onMouseEnter={() => setCursor(d)}
              onClick={() => pick(d)}
              style={{
                ...calDay,
                ...(hi ? { background: 'var(--bg-sunken)' } : null),
                ...(iso === todayIso ? { fontWeight: 700, color: 'var(--brand)' } : null),
                ...(on ? { background: 'var(--brand)', color: 'var(--brand-on)' } : null),
              }}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div style={listFoot}>
        <button type="button" style={linkLike} onClick={() => pick(today())}>
          오늘
        </button>
        <button type="button" style={linkLike} onClick={() => end('', { dr: 1, dc: 0 })}>
          비우기
        </button>
      </div>
    </div>
  );
}

/** 오늘 — 한국 날짜로 정하고(시트에 '오늘'로 들어가는 값), 달력 칸과 같은 로컬 자정 Date 로 만든다. */
function today(): Date {
  return parseIso(kstTodayYmd())!;
}
function parseIso(v: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

const calNav: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  color: 'var(--fg-default)',
  cursor: 'pointer',
};
const calGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 2,
  textAlign: 'center',
};
const calHead: CSSProperties = { fontSize: 11, color: 'var(--fg-muted)', padding: '2px 0' };
const calDay: CSSProperties = {
  height: 28,
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  color: 'var(--fg-default)',
  fontSize: 12,
  cursor: 'pointer',
};

const MARK: CSSProperties = {
  background: 'color-mix(in srgb, var(--brand) 16%, var(--bg-surface))',
  boxShadow: 'inset 0 2px 0 var(--brand), inset 0 -2px 0 var(--brand)',
};
const TONE = {
  pending: 'var(--bg-warning-subtle)',
  problem: 'var(--bg-danger-subtle)',
  draft: 'color-mix(in srgb, var(--brand) 7%, var(--bg-surface))',
} as const;

/** 안 고칠 때 — 보이지 않고 키보드만 받는다 */
const ghostBox: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  border: 'none',
  padding: 0,
  margin: 0,
  resize: 'none',
  overflow: 'hidden',
  caretColor: 'transparent',
  pointerEvents: 'none',
};
const editorBox: CSSProperties = {
  position: 'absolute',
  zIndex: 4,
  boxSizing: 'border-box',
  margin: 0,
  padding: '4px 6px',
  font: 'inherit',
  fontSize: 12,
  lineHeight: '18px',
  color: 'var(--fg-default)',
  background: 'var(--bg-surface)',
  border: '2px solid var(--brand)',
  borderRadius: 0,
  outline: 'none',
  resize: 'none',
};
const inBox: CSSProperties = {
  top: 0,
  left: 0,
  width: '100%',
  minHeight: '100%',
  overflow: 'hidden',
};
const longBox: CSSProperties = {
  top: 0,
  left: 0,
  width: 'max(100%, 360px)',
  height: 140,
  resize: 'both',
  boxShadow: '0 6px 18px rgba(0,0,0,.18)',
};
const listBox: CSSProperties = {
  position: 'fixed',
  zIndex: 1000,
  maxHeight: 320,
  overflowY: 'auto',
  padding: 4,
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 6,
  boxShadow: '0 8px 24px rgba(0,0,0,.18)',
  outline: 'none',
  textAlign: 'left',
};
const listItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 6px',
  borderRadius: 4,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const chipLike: CSSProperties = {
  display: 'inline-block',
  padding: '0 8px',
  borderRadius: 999,
  fontSize: 11,
  lineHeight: '18px',
};
const listFoot: CSSProperties = {
  display: 'flex',
  gap: 8,
  borderTop: '1px solid var(--border-subtle)',
  marginTop: 4,
  padding: '4px 6px 0',
};
const linkLike: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  fontSize: 12,
  color: 'var(--fg-muted)',
  cursor: 'pointer',
};
